import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdPhsInfoService, PhsCategoryInfo } from '@/app/human-design/chart/bodygraph/hd-phs-info.service';
import { PhsCategory } from '@/app/human-design/chart/bodygraph/hd-phs-metadata';
import { phsFaAlternates } from '@/app/human-design/chart/bodygraph/hd-phs-fa-icons';

interface RawRow {
  readonly label: string;
  readonly sub: boolean;
  readonly slot: string;
  readonly defaultFa: string;
}

interface PhsCandidateRow {
  readonly label: string;
  readonly sub: boolean;
  /** FA-kandidaten: default (eerste) + alternatieven. */
  readonly fa: readonly string[];
  /** Default-FA wordt elders ook gebruikt (genuine cross-concept duplicate). */
  readonly duplicate: boolean;
}

interface PhsCandidateGroup {
  readonly title: string;
  readonly rows: readonly PhsCandidateRow[];
}

const CATEGORY_LABELS: Record<PhsCategory, string> = {
  determination: 'Determinatie',
  environment: 'Omgeving',
  view: 'Visie',
  motivation: 'Motivatie',
};

const CATEGORY_ORDER: readonly PhsCategory[] = ['determination', 'environment', 'view', 'motivation'];

/**
 * Visie + motivatie delen de toon-paren, cognitie (bewustzijnsstaten) én het
 * brein — die tellen we als één logische slot zodat alleen écht verschillende
 * keynotes die hetzelfde icoon delen als "dubbel" gemarkeerd worden.
 */
function normalizeSlot(slot: string): string {
  return slot.replace(
    /^(?:view|motivation)(\.color\.\d+\.(?:left|right)|\.cognition\.\d+|\.brain\.(?:left|right))$/,
    'personality$1',
  );
}

/**
 * Vergelijkingstabel: platte, gegroepeerde lijst van alle PHS-keynotes met per
 * keynote de **FontAwesome**-kandidaten (default + alternatieven). Rijen waarvan
 * het default-FA-icoon elders ook gebruikt wordt, krijgen een dubbel-markering.
 * Hover toont per icoon de FA-naam.
 */
@Component({
  selector: 'hd-phs-icon-candidates-page',
  standalone: false,
  templateUrl: './hd-phs-icon-candidates-page.component.html',
  styleUrl: './hd-phs-icon-candidates-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdPhsIconCandidatesPageComponent {
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly phsInfo = inject(HdPhsInfoService);

  constructor() {
    effect(() => this.phsInfo.loadScopesForLang(this.contentLang.effectiveLang()));
  }

  protected readonly groups = computed<readonly PhsCandidateGroup[]>(() => {
    const lang = this.contentLang.effectiveLang();

    // 1. Bouw de ruwe rijen, gegroepeerd.
    const rawGroups: { title: string; rows: RawRow[] }[] = [];
    for (const category of CATEGORY_ORDER) {
      const info = this.phsInfo.categoryInfoIn(category, lang);
      rawGroups.push(this.colorsGroup(category, info));
      rawGroups.push(this.cognitionGroup(category, info));
      rawGroups.push(this.brainGroup(category, info));
    }

    // 2. Welke default-FA-iconen komen in >1 (genormaliseerde) slot voor?
    const slotsByIcon = new Map<string, Set<string>>();
    for (const group of rawGroups) {
      for (const row of group.rows) {
        const set = slotsByIcon.get(row.defaultFa) ?? new Set<string>();
        set.add(normalizeSlot(row.slot));
        slotsByIcon.set(row.defaultFa, set);
      }
    }
    const reused = new Set(
      [...slotsByIcon].filter(([, slots]) => slots.size > 1).map(([icon]) => icon),
    );

    // 3. Verrijk tot de finale rijen (FA-kandidaten + dubbel-vlag).
    return rawGroups.map(group => ({
      title: group.title,
      rows: group.rows.map<PhsCandidateRow>(row => ({
        label: row.label,
        sub: row.sub,
        fa: [row.defaultFa, ...phsFaAlternates(row.slot)],
        duplicate: reused.has(row.defaultFa),
      })),
    }));
  });

  private colorsGroup(category: PhsCategory, info: PhsCategoryInfo): { title: string; rows: RawRow[] } {
    const rows: RawRow[] = [];
    for (const color of info.colors) {
      const base = `${category}.color.${color.number}`;
      rows.push({ label: `${color.number} · ${color.name}`, sub: false, slot: base, defaultFa: color.icon });
      rows.push({ label: `↳ ${color.tone.left.name}`, sub: true, slot: `${base}.left`, defaultFa: color.toneLeftIcon });
      rows.push({ label: `↳ ${color.tone.right.name}`, sub: true, slot: `${base}.right`, defaultFa: color.toneRightIcon });
    }
    return { title: `${CATEGORY_LABELS[category]} — kleuren & tonen`, rows };
  }

  private cognitionGroup(category: PhsCategory, info: PhsCategoryInfo): { title: string; rows: RawRow[] } {
    const rows = info.cognition.map<RawRow>(cog => ({
      label: cog.name,
      sub: false,
      slot: `${category}.cognition.${cog.tone}`,
      defaultFa: cog.icon,
    }));
    return { title: `${CATEGORY_LABELS[category]} — cognitie`, rows };
  }

  private brainGroup(category: PhsCategory, info: PhsCategoryInfo): { title: string; rows: RawRow[] } {
    const rows: RawRow[] = [
      { label: info.brain.left.name, sub: false, slot: `${category}.brain.left`, defaultFa: info.brain.left.icon },
      { label: info.brain.right.name, sub: false, slot: `${category}.brain.right`, defaultFa: info.brain.right.icon },
    ];
    return { title: `${CATEGORY_LABELS[category]} — brein`, rows };
  }
}
